<template>
  <div class="modal" :class="{ 'is-active': isActive }">
    <div class="modal-background" @click="handleCancel"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title section-title">Deactivate Representative</p>
        <button class="delete" aria-label="close" @click="handleCancel"></button>
      </header>
      <section class="modal-card-body">
        <div v-if="errorMessage" class="notification is-danger">
          <button class="delete" @click="errorMessage = ''"></button>
          {{ errorMessage }}
        </div>

        <p class="has-text-weight-semibold" style="margin-bottom: 1.5rem;">
          Are you sure you want to deactivate this representative? This will remove their access to all properties and payments from your account.
        </p>
        <p class="has-text-weight-semibold" style="margin-bottom: 1.5rem;">
          Existing applications won't be affected.
        </p>

        <div class="box" style="background-color: #f9f9f9;">
          <h4 class="title is-5">Representative Details</h4>
          <div class="content">
            <p><strong>Full Name:</strong> {{ rep.ar_full_name || 'N/A' }}</p>
            <p><strong>Email:</strong> {{ rep.ar_email || 'N/A' }}</p>
            <p><strong>Firm:</strong> {{ rep.ar_organization_name || 'N/A' }}</p>
          </div>
        </div>

      </section>
      <footer class="modal-card-foot">
        <div class="buttons">
          <button
            class="button is-danger"
            @click="handleConfirm"
            :class="{ 'is-loading': isLoading }"
            :disabled="isLoading"
          >
            <span class="icon">
              <i class="fas fa-trash"></i>
            </span>
            <span>Deactivate Representative</span>
          </button>
          <button
            class="button is-secondary"
            @click="handleCancel"
            :disabled="isLoading"
          >
            Cancel
          </button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import { handleARDeactivate, handleClearARFromProperties } from '@/utilities/dashboard/authorized-reps.js';

export default {
  name: 'DeactivateRepModal',
  props: {
    isActive: {
      type: Boolean,
      required: true,
    },
    rep: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  data() {
    return {
      isLoading: false,
      errorMessage: '',
    };
  },
  methods: {
    handleCancel() {
      if (!this.isLoading) {
        this.errorMessage = '';
        this.$emit('close');
      }
    },
    async handleConfirm() {
      this.isLoading = true;
      this.errorMessage = '';

      try {
        // 1. Deactivate the AR (set status to "Inactive")
        const deactivateResponse = await handleARDeactivate(this.rep.rid);
        const deactivateData = await deactivateResponse.json();

        if (!deactivateResponse.ok) {
          this.errorMessage = deactivateData.error?.message || 'Failed to deactivate representative';
          this.isLoading = false;
          return;
        }

        // 2. Clear AR from all properties
        const clearResponse = await handleClearARFromProperties(this.rep.rid);
        const clearData = await clearResponse.json();

        if (!clearResponse.ok) {
          this.errorMessage = clearData.error?.message || 'Failed to remove representative from properties';
          this.isLoading = false;
          return;
        }

        // Success! Emit confirm and reset
        this.$emit('confirm', this.rep);
        this.resetModal();
      } catch (error) {
        console.error('Error deactivating representative:', error);
        this.errorMessage = 'An unexpected error occurred. Please try again.';
        this.isLoading = false;
      }
    },
    resetModal() {
      this.isLoading = false;
      this.errorMessage = '';
    },
  },
  watch: {
    isActive(newVal) {
      if (!newVal) {
        this.resetModal();
      }
    },
  },
};
</script>

<style scoped>
.modal {
  z-index: 1003;
}

.modal-background {
  z-index: 1003;
}

.modal-card {
  z-index: 1004;
  max-width: 600px;
  width: 90%;
}

.modal-card-head {
  padding: 0.5rem;
}

.modal-card-body {
  padding: 0.5rem;
}

.modal-card-foot {
  padding: 1rem;
}

.section-title {
  font-size: 1.25rem;
  font-family: 'Montserrat', sans-serif;
  color: #000000;
  margin-bottom: 0;
}

.box {
  margin-bottom: 1rem;
}

.buttons {
  width: 100%;
  justify-content: flex-start;
}
</style>
